import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlanValidation } from '../hooks/usePlanValidation';
import PlanLimitMessage from './PlanLimitMessage';
import { Card } from '../components/ui/card';
import { 
  Building, 
  Home, 
  ArrowRight, 
  DollarSign, 
  Key, 
  Clock, 
  Shield, 
  Medal, 
  UserCheck,
  Building2,
  Star,
  Landmark
} from 'lucide-react';

const SelectionCard = ({ title, description, icon: Icon, benefits, color = "blue", onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Define color schemes for glassmorphism effect
  const colorSchemes = {
    blue: {
      gradient: "from-blue-500 to-blue-600",
      hoverText: "text-blue-600 dark:text-blue-400",
      iconGlow: "bg-blue-500/20",
      accentGlow: "bg-blue-500/30"
    },
    amber: {
      gradient: "from-amber-500 to-amber-600", 
      hoverText: "text-amber-600 dark:text-amber-400",
      iconGlow: "bg-amber-500/20",
      accentGlow: "bg-amber-500/30"
    },
    emerald: {
      gradient: "from-emerald-500 to-emerald-600",
      hoverText: "text-emerald-600 dark:text-emerald-400", 
      iconGlow: "bg-emerald-500/20",
      accentGlow: "bg-emerald-500/30"
    },
    purple: {
      gradient: "from-purple-500 to-purple-600",
      hoverText: "text-purple-600 dark:text-purple-400",
      iconGlow: "bg-purple-500/20", 
      accentGlow: "bg-purple-500/30"
    }
  };

  const colors = colorSchemes[color] || colorSchemes.blue;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative cursor-pointer overflow-hidden h-full
                transition-all duration-500 ease-out
                hover:-translate-y-2 active:scale-[0.98]
                ${isHovered ? 'z-10' : 'z-0'}`}
    >
      {/* Glassmorphism container */}
      <div className={`relative h-full rounded-2xl backdrop-blur-xl border transition-all duration-500
                     ${isHovered 
                       ? 'bg-white/20 dark:bg-white/10 border-white/30 dark:border-white/20 shadow-2xl dark:shadow-black/50' 
                       : 'bg-white/10 dark:bg-white/5 border-white/20 dark:border-white/10 shadow-lg dark:shadow-black/30'
                     }`}>
        
        {/* Gradient overlay on hover */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colors.gradient} transition-opacity duration-500
                       ${isHovered ? 'opacity-5' : 'opacity-0'}`} />
        
        {/* Content */}
        <div className="relative z-10 p-8 h-full flex flex-col">
          {/* Header con ícono y título */}
          <div className="flex items-start gap-4 mb-6">
            <div className="relative">
              {/* Icon glow effect */}
              <div className={`absolute inset-0 ${colors.iconGlow} rounded-xl blur-lg transition-all duration-300
                            ${isHovered ? 'opacity-100 scale-110' : 'opacity-0 scale-100'}`} />
              
              <div className={`relative p-3 bg-gradient-to-br ${colors.gradient} rounded-xl shadow-lg
                            transform transition-all duration-300
                            ${isHovered ? 'scale-110 shadow-xl' : 'scale-100'}`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
            </div>
            
            <h2 className={`text-2xl font-bold transition-all duration-300
                         ${isHovered ? colors.hoverText : 'text-gray-900 dark:text-white'}`}>
              {title}
            </h2>
          </div>

          {/* Descripción */}
          <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed opacity-90">
            {description}
          </p>

          {/* Beneficios */}
          <div className="space-y-3 mb-8 flex-1">
            {benefits && benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className={`p-1.5 rounded-full transition-all duration-300
                               ${isHovered 
                                 ? `bg-gradient-to-r ${colors.gradient} shadow-lg` 
                                 : 'bg-white/20 dark:bg-white/10 backdrop-blur-sm'}`}>
                  <Shield className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-200 opacity-90 leading-relaxed">
                  {benefit}
                </span>
              </div>
            ))}
          </div>

          {/* Botón de acción */}
          <div className="mt-auto">
            <button 
              className={`w-full py-4 px-6 rounded-xl flex items-center justify-center gap-2
                        font-semibold transition-all duration-300 backdrop-blur-sm
                        ${isHovered 
                          ? `bg-gradient-to-r ${colors.gradient} text-white shadow-xl border-0 transform scale-105` 
                          : 'bg-white/20 dark:bg-white/10 text-gray-800 dark:text-white border border-white/30 dark:border-white/20 hover:bg-white/30 dark:hover:bg-white/20'}`}
            >
              Comenzar ahora
              <ArrowRight className={`w-4 h-4 transition-transform duration-300 
                                  ${isHovered ? 'translate-x-1' : ''}`} />
            </button>
          </div>
        </div>

        {/* Accent glow on edges */}
        <div className={`absolute inset-0 rounded-2xl ${colors.accentGlow} transition-opacity duration-500
                       ${isHovered ? 'opacity-20' : 'opacity-0'}`} 
             style={{
               background: `linear-gradient(45deg, transparent 30%, ${colors.accentGlow.replace('bg-', '').replace('/30', '')}/10 50%, transparent 70%)`
             }} />
        
        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${colors.gradient} rounded-full
                       transition-all duration-500 blur-sm
                       ${isHovered ? 'w-full opacity-80' : 'w-0 opacity-0'}`} />
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
    <div className="p-4 bg-white dark:bg-gray-800 rounded-full shadow-md dark:shadow-xl">
      <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
    </div>
  </div>
);

// Componente para mostrar cuando el usuario es un cliente sin plan
const ClientMessage = ({ onUpgrade }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4 flex items-center justify-center">
    <div className="max-w-md w-full mx-auto relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute -z-10 inset-0">
        <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-blue-400/30 to-indigo-400/30 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-indigo-400/30 to-blue-400/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2 animate-pulse"></div>
      </div>

      {/* Glassmorphism container */}
      <div className="relative backdrop-blur-xl bg-white/20 dark:bg-white/10 rounded-2xl border border-white/30 dark:border-white/20 shadow-2xl dark:shadow-black/50 p-10">
        {/* Gradient overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 to-indigo-500/5 dark:from-blue-400/10 dark:to-indigo-400/10" />
        
        <div className="relative z-10">
          <div className="mb-6 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 backdrop-blur-sm p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto shadow-lg border border-white/20">
            <Building2 className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 dark:from-blue-300 dark:to-indigo-300 text-center">
            Sin Plan Activo
          </h3>
          <p className="text-gray-700 dark:text-gray-200 mb-8 text-center leading-relaxed opacity-90">
            Como cliente, no tienes un plan activo. Actualiza a vendedor para publicar propiedades y comenzar a ofrecer tus inmuebles al mercado.
          </p>
          <button
            onClick={onUpgrade}
            className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 flex items-center justify-center gap-2 backdrop-blur-sm"
          >
            <Star className="w-5 h-5" />
            <span>Convertirme en Vendedor</span>
          </button>
        </div>
      </div>
    </div>
  </div>
);

const PropertySelection = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { canPublish, loading, planInfo } = usePlanValidation();
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    // Verificar si el usuario tiene plan premium o premium_constructora
    const checkPremiumStatus = async () => {
      try {
        // Primero verificamos si la información del plan ya está en planInfo
        if (planInfo && planInfo.plan) {
          const planLower = planInfo.plan.toLowerCase();
          setIsPremium(planLower === 'premium' || planLower === 'premium_constructora');
          return;
        }

        // Si no está en planInfo, consultamos directamente a la API
        const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/plan-info', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Si el plan es premium o premium_constructora, activamos la funcionalidad
          if (data.plan) {
            const planLower = data.plan.toLowerCase();
            setIsPremium(planLower === 'premium' || planLower === 'premium_constructora');
          }
        }
      } catch (error) {
        console.error('Error al verificar el estado premium:', error);
        
        // Fallback: verificar con datos del usuario directamente
        if (user && user.plan) {
          const planLower = user.plan.toLowerCase();
          setIsPremium(planLower === 'premium' || planLower === 'premium_constructora');
        }
      }
    };

    if (isAuthenticated && user) {
      checkPremiumStatus();
    }
  }, [user, isAuthenticated, planInfo]);

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si está cargando, mostrar spinner
  if (loading) {
    return <LoadingSpinner />;
  }

  // Si el usuario es cliente (tipo_usuario_id = 1), mostrar mensaje de cliente
  if (user?.tipo_usuario_id === 1) {
    return <ClientMessage onUpgrade={() => navigate('/upgrade-user')} />;
  }

  // Mostrar mensaje de límite de plan si no puede publicar
  if (!canPublish) {
    return (
      <PlanLimitMessage 
        planInfo={{
          typeId: user?.tipo_usuario_id,
          plan: user?.plan || 'gratuito',
          totalProperties: planInfo?.totalProperties || 0,
          limit: planInfo?.limit || 0
        }}
        onUpgrade={() => navigate('/upgrade-plan')}
        onManage={() => navigate('/mis_propiedades')}
      />
    );
  }

  const handleSelection = (operationType, publicationType = 'propiedad') => {
    navigate(`/publicar/${operationType}/${publicationType}`);
  };

  // Beneficios por tipo de operación
  const saleBenefits = [
    "Mayor visibilidad en el mercado inmobiliario",
    "Conexión directa con compradores potenciales",
    "Proceso de venta sin intermediarios",
    "Destaca las mejores características de tu propiedad"
  ];

  const rentalBenefits = [
    "Encuentra inquilinos verificados rápidamente",
    "Gestión sencilla de solicitudes de alquiler",
    "Mejora la rentabilidad de tu propiedad",
    "Proceso de alquiler seguro y transparente"
  ];

  const proyectoBenefits = [
    "Promociona proyectos inmobiliarios en desarrollo",
    "Capta inversores y compradores anticipados",
    "Gestiona múltiples unidades en un solo lugar",
    "Ofrece financiamiento bancario a tus clientes"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-700 dark:text-blue-300 text-sm font-medium mb-4">
            <Medal className="w-4 h-4" />
            <span>PUBLICACIÓN DE PROPIEDADES</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            ¿Qué tipo de operación deseas hacer?<br />
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Elige el tipo de operación que deseas realizar y comienza a publicar tu propiedad 
            en nuestra plataforma de manera rápida y sencilla.
          </p>
        </div>

        {/* Selection cards */}
        <div className={`grid ${isPremium ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'} gap-8 max-w-6xl mx-auto`}>
          <SelectionCard
            title="Vender Propiedad"
            description="Publica tu propiedad para venta y encuentra compradores interesados. Nuestro proceso simplificado te ayudará a realizar la transacción de manera eficiente."
            icon={DollarSign}
            benefits={saleBenefits}
            color="blue"
            onClick={() => handleSelection('comprar')}
          />

          <SelectionCard
            title="Alquilar Propiedad"
            description="Publica tu propiedad para alquiler y encuentra inquilinos confiables. Gestiona las solicitudes y arrienda tu propiedad de forma segura y rentable."
            icon={Key}
            benefits={rentalBenefits}
            color="emerald"
            onClick={() => handleSelection('alquiler')}
          />

          {/* Mostrar tarjeta de proyecto si tiene plan premium */}
          {isPremium && (
            <SelectionCard
              title="Publicar Proyecto"
              description="Publica tus proyectos inmobiliarios en desarrollo. Promociona nuevas urbanizaciones, edificios de apartamentos y más."
              icon={Landmark}
              benefits={proyectoBenefits}
              color="purple"
              onClick={() => handleSelection('comprar', 'proyecto')}
            />
          )}
        </div>
        
        {/* Features section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
              ¿Por qué elegir InKayros para tus operaciones inmobiliarias?
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Te ofrecemos las mejores herramientas para gestionar tus propiedades
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "Rapidez", desc: "Resultados en menos tiempo" },
              { icon: Shield, title: "Seguridad", desc: "Operaciones verificadas" },
              { icon: UserCheck, title: "Confianza", desc: "Usuarios verificados" },
              { icon: Building, title: "Visibilidad", desc: "Mayor alcance de mercado" }
            ].map((feature, index) => (
              <div key={index} className="group relative">
                {/* Glassmorphism card */}
                <div className="backdrop-blur-xl bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 p-6 rounded-2xl flex flex-col items-center text-center transition-all duration-300 hover:bg-white/20 dark:hover:bg-white/10 hover:-translate-y-1 hover:shadow-xl dark:hover:shadow-black/30">
                  <div className="p-4 bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl mb-4 border border-white/20 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-bold text-gray-800 dark:text-white mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 opacity-90">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA section */}
        <div className="mt-16 text-center">
          <div className="relative backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl p-8 border border-white/20 dark:border-white/10 shadow-xl dark:shadow-black/30">
            {/* Gradient overlay */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-purple-500/5 dark:from-blue-400/10 dark:to-purple-400/10" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                ¿Listo para comenzar?
              </h3>
              <p className="text-gray-700 dark:text-gray-200 mb-6 opacity-90">
                Únete a miles de propietarios que ya confían en nuestra plataforma para sus operaciones inmobiliarias.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button 
                  onClick={() => handleSelection('comprar')}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 backdrop-blur-sm"
                >
                  Vender Propiedad
                </button>
                <button 
                  onClick={() => handleSelection('alquiler')}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 backdrop-blur-sm"
                >
                  Alquilar Propiedad
                </button>
                {isPremium && (
                  <button 
                    onClick={() => handleSelection('comprar', 'proyecto')}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 backdrop-blur-sm"
                  >
                    Publicar Proyecto
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertySelection;